import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LayoutTemplate, Copy, Pencil, Trash2, Plus, Save, X, AlertTriangle,
  CheckCircle2, Lock, Package, ChevronLeft,
} from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import {
  onboardingApi,
  type OnboardingTemplateDto,
  type SetupPackDto,
} from '@/api/onboarding.api';
import {
  parseDefinition, advancedJson, withAdvanced, summarise,
  type TemplateDefinition, type RoleBinding, type TenderMode, type AccountSpec,
} from './templateDefinition';

const inputCls =
  'w-full px-2.5 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 ' +
  'text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const OnboardingTemplatesPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.ONBOARDING_TEMPLATE_MANAGE);

  const [templates, setTemplates] = useState<OnboardingTemplateDto[]>([]);
  const [packs, setPacks]         = useState<SetupPackDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [toast, setToast]         = useState<string | null>(null);

  // Editing state — null means the list is showing.
  const [editing, setEditing]   = useState<OnboardingTemplateDto | null>(null);
  const [isClone, setIsClone]   = useState(false);
  const [code, setCode]         = useState('');
  const [name, setName]         = useState('');
  const [description, setDescription] = useState('');
  const [def, setDef]           = useState<TemplateDefinition>({});
  const [advanced, setAdvanced] = useState('');
  const [advErr, setAdvErr]     = useState<string | null>(null);
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<OnboardingTemplateDto | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([onboardingApi.templates(), onboardingApi.packs()]);
      setTemplates(t); setPacks(p);
    } catch { setError('Could not load templates.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  const rulePacks = useMemo(() => packs.filter(p => p.packType === 'POSTING_RULES'), [packs]);
  const coaPacks  = useMemo(() => packs.filter(p => p.packType === 'COA'), [packs]);

  const openEditor = (t: OnboardingTemplateDto, clone: boolean) => {
    const { def: parsed, error: perr } = parseDefinition(t.definition);
    if (!parsed) { setError(`This template's definition could not be read: ${perr}`); return; }
    setEditing(t);
    setIsClone(clone);
    setCode(clone ? `${t.templateCode.replace(/_V\d+$/, '')}_CUSTOM` : t.templateCode);
    setName(clone ? `${t.templateName} (copy)` : t.templateName);
    setDescription(t.description ?? '');
    setDef(parsed);
    setAdvanced(advancedJson(parsed));
    setAdvErr(null);
    setError(null);
  };

  const openNew = () => {
    const blank: TemplateDefinition = {
      schemaVersion: 1, industry: 'GENERIC',
      baseSetup: { coaPack: coaPacks[0]?.packCode ?? 'STANDARD_LK_V1', currencies: ['LKR'],
                   financialYears: { mode: 'GENERATE', fiscalStartMonth: 1, yearsBack: 1, yearsForward: 1, periodType: 'MONTHLY' },
                   createIntegrationApiKey: true, createAdminUser: true },
      accounts: [], companyRoles: [], tenders: { modes: [] },
      parties: { supplier: {}, customer: {} },
      postingRulePacks: [], activateRules: true,
    };
    setEditing({ id: '', templateCode: '', templateName: '', industry: 'GENERIC', versionNo: 1,
                 isSystem: false, isActive: true, definition: '{}', createdDate: '' });
    setIsClone(true);          // a new template is created, not updated
    setCode(''); setName(''); setDescription('');
    setDef(blank); setAdvanced('{}'); setAdvErr(null); setError(null);
  };

  const closeEditor = () => { setEditing(null); setError(null); };

  const patch = (p: Partial<TemplateDefinition>) => setDef(d => ({ ...d, ...p }));

  const save = async () => {
    // Advanced JSON is merged last so a syntax error stops the save rather than losing sections.
    let merged = def;
    if (advanced.trim()) {
      const { def: adv, error: aerr } = parseDefinition(advanced);
      if (!adv) { setAdvErr(aerr); return; }
      merged = withAdvanced(def, adv as Record<string, unknown>);
    } else {
      merged = withAdvanced(def, {});
    }
    setAdvErr(null);

    if (!code.trim() || !name.trim()) { setError('A code and a name are required.'); return; }

    setSaving(true); setError(null);
    try {
      const req = {
        templateCode: code.trim().toUpperCase(),
        templateName: name.trim(),
        description: description.trim() || null,
        industry: (merged.industry ?? 'GENERIC').toString(),
        definition: JSON.stringify(merged),
      };
      if (isClone || !editing?.id) await onboardingApi.createTemplate(req);
      else                          await onboardingApi.updateTemplate(editing.id, req);
      await load();
      closeEditor();
      flash(isClone || !editing?.id ? 'Template created.' : 'Template updated.');
    } catch (e) {
      setError(errorText(e, 'Save failed.'));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await onboardingApi.deleteTemplate(confirmDelete.id);
      await load();
      flash('Template retired.');
    } catch (e) { setError(errorText(e, 'Could not retire the template.')); }
    finally { setConfirmDelete(null); }
  };

  // ── List ──────────────────────────────────────────────────────────────────
  if (!editing) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 flex items-start gap-3">
          <LayoutTemplate size={22} className="text-signal mt-0.5" />
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Onboarding templates</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 max-w-3xl">
              What a company gets when it is provisioned: its chart of accounts, the account each
              posting role resolves to, its tender modes, and which posting-rule packs are installed.
              Shipped templates are read-only — copy one to tailor it for a client.
            </p>
          </div>
          {canManage && (
            <button onClick={openNew}
              className="px-3.5 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg flex items-center gap-1.5 flex-shrink-0">
              <Plus size={15} /> New template
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {toast && (
            <div className="max-w-3xl mb-4 flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 dark:border-teal-800/50 dark:bg-teal-900/20 px-3 py-2 text-sm text-teal-800 dark:text-teal-200">
              <CheckCircle2 size={15} /> {toast}
            </div>
          )}
          {error && (
            <div className="max-w-3xl mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-800 dark:text-rose-200">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><Spinner size="lg" /></div>
          ) : (
            <div className="max-w-3xl space-y-2">
              {templates.map(t => (
                <div key={t.id} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">{t.templateName}</span>
                    <code className="text-xs font-mono text-slate-400">{t.templateCode}</code>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {t.industry}
                    </span>
                    {t.isSystem && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <Lock size={10} /> Shipped
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      {canManage && (
                        <button onClick={() => openEditor(t, true)}
                          className="px-2 py-1 text-xs font-medium rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-1">
                          <Copy size={12} /> Copy
                        </button>
                      )}
                      {canManage && !t.isSystem && (<>
                        <button onClick={() => openEditor(t, false)}
                          className="px-2 py-1 text-xs font-medium rounded text-signal hover:bg-signal/10 flex items-center gap-1">
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => setConfirmDelete(t)}
                          className="px-2 py-1 text-xs font-medium rounded text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-1">
                          <Trash2 size={12} /> Retire
                        </button>
                      </>)}
                    </div>
                  </div>
                  {t.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{t.description}</p>}
                  <p className="text-xs font-mono text-slate-400 mt-2">{summarise(t.definition)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmDelete(null)}>
            <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Retire template</h3>
              </div>
              <div className="p-5 text-sm text-slate-600 dark:text-slate-300 space-y-2">
                <p>
                  Retire <strong>{confirmDelete.templateName}</strong>? It stops appearing here and in the
                  onboarding wizard.
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Companies already provisioned from it are unaffected — a template is only read at
                  provisioning time. The record is kept rather than erased, so the run history stays
                  readable; creating a template with code
                  <code className="mx-1 font-mono">{confirmDelete.templateCode}</code>
                  again will revive this one.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => setConfirmDelete(null)}
                  className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                <button onClick={doDelete}
                  className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-500 rounded-lg">Retire</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Editor ────────────────────────────────────────────────────────────────
  const roles  = def.companyRoles ?? [];
  const modes  = def.tenders?.modes ?? [];
  const extras = def.accounts ?? [];
  const chosen = def.postingRulePacks ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
        <button onClick={closeEditor} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
          <ChevronLeft size={18} className="text-slate-500" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">
            {isClone || !editing.id ? 'New template' : `Edit ${editing.templateName}`}
          </h1>
          {isClone && editing.id && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Copied from <code className="font-mono">{editing.templateCode}</code>. Saving creates a new
              template; the original is untouched.
            </p>
          )}
        </div>
        <button onClick={closeEditor}
          className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
          Cancel
        </button>
        <button onClick={save} disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 flex items-center gap-1.5">
          {saving ? <><Spinner size="sm" /> Saving…</> : <><Save size={15} /> Save template</>}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="max-w-3xl mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-800 dark:text-rose-200">
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <div className="max-w-3xl space-y-4">
          {/* Identity */}
          <Section title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Template code">
                <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={50}
                  placeholder="GROCERY_CUSTOM" className={cn(inputCls, 'font-mono')} />
              </Field>
              <Field label="Template name">
                <input value={name} onChange={e => setName(e.target.value)} maxLength={200}
                  placeholder="Grocery — Acme variant" className={inputCls} />
              </Field>
              <Field label="Industry">
                <input value={String(def.industry ?? '')} onChange={e => patch({ industry: e.target.value.toUpperCase() })}
                  maxLength={40} placeholder="GROCERY" className={cn(inputCls, 'font-mono')} />
              </Field>
              <Field label="Description">
                <input value={description} onChange={e => setDescription(e.target.value)} className={inputCls} />
              </Field>
            </div>
          </Section>

          {/* Base setup */}
          <Section title="Base setup">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Chart of accounts pack">
                <select value={def.baseSetup?.coaPack ?? ''} className={inputCls}
                  onChange={e => patch({ baseSetup: { ...def.baseSetup, coaPack: e.target.value } })}>
                  {coaPacks.map(p => <option key={p.id} value={p.packCode}>{p.packCode} ({p.itemCount})</option>)}
                </select>
              </Field>
              <Field label="Fiscal year starts">
                <select value={def.baseSetup?.financialYears?.fiscalStartMonth ?? 1} className={inputCls}
                  onChange={e => patch({ baseSetup: { ...def.baseSetup,
                    financialYears: { ...def.baseSetup?.financialYears, fiscalStartMonth: Number(e.target.value) } } })}>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="Currencies" hint="Comma separated">
                <input value={(def.baseSetup?.currencies ?? []).join(', ')} className={inputCls}
                  onChange={e => patch({ baseSetup: { ...def.baseSetup,
                    currencies: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) } })} />
              </Field>
            </div>
            <div className="flex items-center gap-5 mt-3">
              <Toggle label="Create an integration API key"
                checked={def.baseSetup?.createIntegrationApiKey !== false}
                onChange={v => patch({ baseSetup: { ...def.baseSetup, createIntegrationApiKey: v } })} />
              <Toggle label="Create an administrator user"
                checked={def.baseSetup?.createAdminUser !== false}
                onChange={v => patch({ baseSetup: { ...def.baseSetup, createAdminUser: v } })} />
              <Toggle label="Activate rules on install"
                checked={def.activateRules !== false}
                onChange={v => patch({ activateRules: v })} />
            </div>
          </Section>

          {/* Posting-rule packs */}
          <Section title="Posting-rule packs"
            hint="Which event types the provisioned company can post. A pack left out means those events fail with “no matching rule”.">
            <div className="grid grid-cols-2 gap-1.5">
              {rulePacks.map(p => {
                const on = chosen.includes(p.packCode);
                return (
                  <label key={p.id}
                    className={cn('flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors',
                      on ? 'border-signal bg-signal/5' : 'border-slate-200 dark:border-slate-700 hover:border-signal/40')}>
                    <input type="checkbox" checked={on} className="mt-0.5"
                      onChange={() => patch({ postingRulePacks: on
                        ? chosen.filter(c => c !== p.packCode)
                        : [...chosen, p.packCode] })} />
                    <span className="min-w-0">
                      <span className="block text-xs font-mono font-semibold text-slate-700 dark:text-slate-200">{p.packCode}</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">{p.packName} · {p.itemCount} rules</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Section>

          {/* Company roles */}
          <Section title="Account roles"
            hint="Every posting rule resolves an account through one of these. A role a rule needs but nothing binds will block provisioning.">
            <Table headers={['Role', 'Voucher category', 'Account code', '']}>
              {roles.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                  <td className="py-1 pr-2">
                    <input value={r.role} className={cn(inputCls, 'font-mono')}
                      onChange={e => patch({ companyRoles: replaceAt(roles, i, { ...r, role: e.target.value.toUpperCase() }) })} />
                  </td>
                  <td className="py-1 pr-2 w-36">
                    <input value={r.scopeCode ?? ''} placeholder="All" className={cn(inputCls, 'font-mono')}
                      title="Leave empty for the company default. A voucher category (e.g. EXCHANGE) gets its own account."
                      onChange={e => {
                        const code = e.target.value.toUpperCase();
                        patch({ companyRoles: replaceAt(roles, i, {
                          ...r, scopeCode: code || null, scopeType: code ? 'VOUCHER_GROUP' : null,
                        }) });
                      }} />
                  </td>
                  <td className="py-1 pr-2 w-32">
                    <input value={r.accountCode} className={cn(inputCls, 'font-mono')}
                      onChange={e => patch({ companyRoles: replaceAt(roles, i, { ...r, accountCode: e.target.value }) })} />
                  </td>
                  <td className="py-1 w-8">
                    <RemoveBtn onClick={() => patch({ companyRoles: removeAt(roles, i) })} />
                  </td>
                </tr>
              ))}
            </Table>
            <AddBtn label="Add role" onClick={() => patch({ companyRoles: [...roles, { role: '', accountCode: '' } as RoleBinding] })} />
          </Section>

          {/* Tenders */}
          <Section title="Tender modes"
            hint="How money taken at the till reaches the ledger. The code must match what the external system sends as tender.modeCode.">
            <Table headers={['Code', 'Name', 'Type', 'Clearing a/c', 'MDR rate', 'MDR expense a/c', '']}>
              {modes.map((m, i) => {
                const set = (p: Partial<TenderMode>) =>
                  patch({ tenders: { ...def.tenders, modes: replaceAt(modes, i, { ...m, ...p }) } });
                return (
                  <tr key={i} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                    <td className="py-1 pr-2 w-24"><input value={m.code} className={cn(inputCls, 'font-mono')} onChange={e => set({ code: e.target.value.toUpperCase() })} /></td>
                    <td className="py-1 pr-2"><input value={m.name ?? ''} className={inputCls} onChange={e => set({ name: e.target.value })} /></td>
                    <td className="py-1 pr-2 w-32"><input value={m.modeType ?? ''} className={cn(inputCls, 'font-mono')} onChange={e => set({ modeType: e.target.value })} /></td>
                    <td className="py-1 pr-2 w-24"><input value={m.clearingAccountCode ?? ''} className={cn(inputCls, 'font-mono')} onChange={e => set({ clearingAccountCode: e.target.value })} /></td>
                    <td className="py-1 pr-2 w-20">
                      <input value={m.mdrRate ?? ''} type="number" step="0.0001" className={cn(inputCls, 'font-mono')}
                        onChange={e => set({ mdrRate: e.target.value === '' ? undefined : Number(e.target.value) })} />
                    </td>
                    <td className="py-1 pr-2 w-24"><input value={m.mdrExpenseAccountCode ?? ''} className={cn(inputCls, 'font-mono')} onChange={e => set({ mdrExpenseAccountCode: e.target.value })} /></td>
                    <td className="py-1 w-8"><RemoveBtn onClick={() => patch({ tenders: { ...def.tenders, modes: removeAt(modes, i) } })} /></td>
                  </tr>
                );
              })}
            </Table>
            <AddBtn label="Add tender mode"
              onClick={() => patch({ tenders: { ...def.tenders, modes: [...modes, { code: '', name: '', modeType: 'card' } as TenderMode] } })} />
          </Section>

          {/* Parties */}
          <Section title="Party control accounts">
            <div className="grid grid-cols-4 gap-3">
              <Field label="Supplier control a/c">
                <input value={def.parties?.supplier?.controlAccountCode ?? ''} className={cn(inputCls, 'font-mono')}
                  onChange={e => patch({ parties: { ...def.parties, supplier: { ...def.parties?.supplier, controlAccountCode: e.target.value } } })} />
              </Field>
              <Field label="Supplier credit days">
                <input type="number" value={def.parties?.supplier?.defaultCreditDays ?? 0} className={inputCls}
                  onChange={e => patch({ parties: { ...def.parties, supplier: { ...def.parties?.supplier, defaultCreditDays: Number(e.target.value) } } })} />
              </Field>
              <Field label="Customer control a/c">
                <input value={def.parties?.customer?.controlAccountCode ?? ''} className={cn(inputCls, 'font-mono')}
                  onChange={e => patch({ parties: { ...def.parties, customer: { ...def.parties?.customer, controlAccountCode: e.target.value } } })} />
              </Field>
              <Field label="Customer credit days">
                <input type="number" value={def.parties?.customer?.defaultCreditDays ?? 0} className={inputCls}
                  onChange={e => patch({ parties: { ...def.parties, customer: { ...def.parties?.customer, defaultCreditDays: Number(e.target.value) } } })} />
              </Field>
            </div>
          </Section>

          {/* Industry accounts */}
          <Section title="Extra accounts"
            hint="Added on top of the chart-of-accounts pack — the accounts this industry needs and the standard chart does not carry.">
            <Table headers={['Code', 'Name', 'Type', 'Parent', 'Posting', '']}>
              {extras.map((a, i) => {
                const set = (p: Partial<AccountSpec>) => patch({ accounts: replaceAt(extras, i, { ...a, ...p }) });
                return (
                  <tr key={i} className="border-b border-slate-50 dark:border-slate-700/40 last:border-0">
                    <td className="py-1 pr-2 w-20"><input value={a.code} className={cn(inputCls, 'font-mono')} onChange={e => set({ code: e.target.value })} /></td>
                    <td className="py-1 pr-2"><input value={a.name} className={inputCls} onChange={e => set({ name: e.target.value })} /></td>
                    <td className="py-1 pr-2 w-28"><input value={a.type} className={cn(inputCls, 'font-mono')} onChange={e => set({ type: e.target.value })} /></td>
                    <td className="py-1 pr-2 w-20"><input value={a.parentCode ?? ''} className={cn(inputCls, 'font-mono')} onChange={e => set({ parentCode: e.target.value || null })} /></td>
                    <td className="py-1 pr-2 w-16 text-center">
                      <input type="checkbox" checked={a.isPosting !== false} onChange={e => set({ isPosting: e.target.checked })} />
                    </td>
                    <td className="py-1 w-8"><RemoveBtn onClick={() => patch({ accounts: removeAt(extras, i) })} /></td>
                  </tr>
                );
              })}
            </Table>
            <AddBtn label="Add account"
              onClick={() => patch({ accounts: [...extras, { code: '', name: '', type: 'Expense', nature: 'Debit', isPosting: true } as AccountSpec] })} />
          </Section>

          {/* Advanced */}
          <Section title="Advanced sections"
            hint="Everything the forms above do not cover — category strategy, account generators, dimensions. Edited as JSON, and preserved exactly if you do not touch it.">
            <textarea value={advanced} onChange={e => { setAdvanced(e.target.value); setAdvErr(null); }} rows={12} spellCheck={false}
              className="w-full font-mono text-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-3 text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-signal" />
            {advErr && (
              <p className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1 mt-1">
                <AlertTriangle size={14} /> {advErr}
              </p>
            )}
          </Section>

          <div className="flex items-center gap-2 pb-6">
            <button onClick={save} disabled={saving}
              className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-40 flex items-center gap-1.5">
              {saving ? <><Spinner size="sm" /> Saving…</> : <><Save size={15} /> Save template</>}
            </button>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Package size={13} /> Preview it from Onboard a Company before using it on a client.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── small building blocks ────────────────────────────────────────────────────
const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
    <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
    {hint && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 mb-2">{hint}</p>}
    <div className={hint ? '' : 'mt-2'}>{children}</div>
  </div>
);

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    {label}
  </label>
);

const Table = ({ headers, children }: { headers: string[]; children: React.ReactNode }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-slate-400 border-b border-slate-100 dark:border-slate-700/60">
          {headers.map((h, i) => <th key={i} className="py-1 pr-2 font-semibold">{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const RemoveBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} title="Remove"
    className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20">
    <X size={14} />
  </button>
);

const AddBtn = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button onClick={onClick}
    className="mt-2 text-xs font-medium text-signal hover:underline flex items-center gap-1">
    <Plus size={13} /> {label}
  </button>
);

const replaceAt = <T,>(arr: T[], i: number, v: T): T[] => arr.map((x, j) => (j === i ? v : x));
const removeAt  = <T,>(arr: T[], i: number): T[] => arr.filter((_, j) => j !== i);

const errorText = (e: unknown, fallback: string): string => {
  const res = (e as { response?: { data?: { message?: string; errors?: Array<{ message: string }> } } })?.response;
  return res?.data?.errors?.[0]?.message ?? res?.data?.message ?? fallback;
};
